/* import __COLOCATED_TEMPLATE__ from './first-time-ux-tooltip.hbs.js'; */
var _dec, _dec2, _class, _descriptor, _descriptor2;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { get as getFromLocalStorage, set as setToLocalStorage } from '@square/dashboard-utilities/utils/persistable';
import { hasConsent, OneTrustConsent } from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
const LOCAL_STORAGE_PREFIX = 'dashboard.first-time-ux';
/**
 * A `market-tooltip` wrapper to display First-Time User Experience:
 * Checks local storage for the passed-in `token` to determine whether to
 * show the tooltip. The tooltip content is shown until manually dismissed.
 * Nothing is shown if localStorage cannot be accessed to avoid unskippable
 * tooltips.
 *
 * Example usage:
 * <First-Time-Ux::First-Time-Ux-Tooltip
 *   @token={{"itemLibrary.selectAllHint"}}>
 *   <:trigger>
 *     <SelectAllCheckbox ... />
 *   </:trigger>
 *   <:content>
 *     <SelectAllHint ... />
 *   </:content>
 * </First-Time-Ux::First-Time-Ux-Tooltip>
 *
 * @class FirstTimeUXTooltip
 * @extends {Component}
 * @param {MarketTooltip} [multiple] Optional MarketTooltip props to be passed down
 *                        to the market-tooltip component. `interaction` and `disabled`
 *                        recommended to leave at default for FTUX.
 * @param {String} [token] Token used to determine whether to show FTUX.
 * @param {String} [slot] Optional slot to pass to the market-tooltip component.
 * @param {Boolean} [hideDismiss] Optional flag to prevent the "Dismiss" link in the content
 *                        from rendering. Default false
 * @param {Number} [activationDelayMs] Optional to control the wait between first render and
 *                        opening the tooltip. May need to be increased depending on the nature
 *                        of the trigger. Default 100
 * @slot trigger - Required component to render the tooltip around.
 * @slot content - Required component to render within the tooltip.
 */
let FirstTimeUXTooltip = (_dec = computed('args.disabled', 'hasCookieConsent', 'previouslyDismissed'), _dec2 = computed('token'), _class = class FirstTimeUXTooltip extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "hasCookieConsent", _descriptor, this);
    _initializerDefineProperty(this, "previouslyDismissed", _descriptor2, this);
  }
  // Props (passed in or default)
  get token() {
    const argToken = this.args.token ?? '';
    return `${LOCAL_STORAGE_PREFIX}.${argToken}`;
  }
  get tooltipDisabled() {
    return this.args.disabled || this.previouslyDismissed || !this.hasCookieConsent;
  }
  get tooltipInteraction() {
    return this.args.interaction || 'persistent';
  }
  get tooltipPopoverDistance() {
    return this.args.popoverDistance ?? 8;
  }
  get tooltipPopoverPlacement() {
    return this.args.popoverPlacement || 'auto';
  }
  get tooltipPopoverSkidding() {
    return this.args.popoverSkidding;
  }
  get tooltipPopoverStrategy() {
    return this.args.popoverStrategy || 'absolute';
  }
  get hideDismiss() {
    return this.args.hideDismiss ?? false;
  }
  get activationDelayMs() {
    return this.args.activationDelayMs ?? 100;
  }
  get triggerElementId() {
    // Replace dots in the token as they interfere with query selection due to ambiguity with CSS class names
    // We can replace the split/join with replaceAll once we compile to es2021+
    return `${this.token.split('.').join('-')}-tooltip-id`;
  }
  onTooltipDismiss() {
    this.previouslyDismissed = true;
    setToLocalStorage(this.token, true, OneTrustConsent.FUNCTIONAL);
  }
  dismissTooltip() {
    this.onTooltipDismiss();
    this.toggleVisible();
  }
  forceDisplayTooltip() {
    // delay buffer to ensure any animations can complete
    setTimeout(() => {
      this.toggleVisible();
    }, this.activationDelayMs);
  }
  toggleVisible() {
    const trigger = this.getTriggerElement();
    trigger?.click();
  }
  getTriggerElement() {
    const selector = `#${this.triggerElementId}`;
    return document.querySelector(selector);
  }
}, _applyDecoratedDescriptor(_class.prototype, "tooltipDisabled", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "tooltipDisabled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "triggerElementId", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "triggerElementId"), _class.prototype), _descriptor = _applyDecoratedDescriptor(_class.prototype, "hasCookieConsent", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return hasConsent(OneTrustConsent.FUNCTIONAL);
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "previouslyDismissed", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return Boolean(getFromLocalStorage(this.token));
  }
}), _applyDecoratedDescriptor(_class.prototype, "onTooltipDismiss", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onTooltipDismiss"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dismissTooltip", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dismissTooltip"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "forceDisplayTooltip", [action], Object.getOwnPropertyDescriptor(_class.prototype, "forceDisplayTooltip"), _class.prototype), _class);
export { FirstTimeUXTooltip as default };